/**
 * Suppliers: who we buy from. Add, edit, disable — nothing more, because a
 * purchase order only needs a name, where to send it and where to pay.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, CheckCircle2, Pencil, Plus, Search, Truck } from 'lucide-react';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

interface SupplierRow {
  id: string;
  code: string;
  name: string;
  tin: string | null;
  addressLine: string | null;
  city: string | null;
  contactPerson: string | null;
  phone: string | null;
  email: string | null;
  bankAccountName: string | null;
  bankName: string | null;
  bankAccountNumber: string | null;
  bankDetails: string | null;
  paymentTermsDays: number | null;
  notes: string | null;
  status: 'ACTIVE' | 'DISABLED';
  _count: { purchaseOrders: number; quotations: number; apEntries: number };
}

type Draft = {
  name: string;
  tin: string;
  addressLine: string;
  city: string;
  contactPerson: string;
  phone: string;
  email: string;
  bankAccountName: string;
  bankName: string;
  bankAccountNumber: string;
  bankDetails: string;
  paymentTermsDays: string;
  notes: string;
};

const EMPTY: Draft = {
  name: '',
  tin: '',
  addressLine: '',
  city: '',
  contactPerson: '',
  phone: '',
  email: '',
  bankAccountName: '',
  bankName: '',
  bankAccountNumber: '',
  bankDetails: '',
  paymentTermsDays: '',
  notes: '',
};

const toDraft = (row: SupplierRow): Draft => ({
  name: row.name,
  tin: row.tin ?? '',
  addressLine: row.addressLine ?? '',
  city: row.city ?? '',
  contactPerson: row.contactPerson ?? '',
  phone: row.phone ?? '',
  email: row.email ?? '',
  bankAccountName: row.bankAccountName ?? '',
  bankName: row.bankName ?? '',
  bankAccountNumber: row.bankAccountNumber ?? '',
  bankDetails: row.bankDetails ?? '',
  paymentTermsDays: row.paymentTermsDays === null ? '' : String(row.paymentTermsDays),
  notes: row.notes ?? '',
});

/** Empty strings are not values; send them as nothing at all. */
const payload = (draft: Draft): Record<string, unknown> => {
  const out: Record<string, unknown> = { name: draft.name.trim() };
  const text: (keyof Draft)[] = [
    'tin',
    'addressLine',
    'city',
    'contactPerson',
    'phone',
    'email',
    'bankAccountName',
    'bankName',
    'bankAccountNumber',
    'bankDetails',
    'notes',
  ];
  for (const key of text) {
    const value = draft[key].trim();
    if (value) out[key] = value;
  }
  if (draft.paymentTermsDays.trim()) out.paymentTermsDays = Number(draft.paymentTermsDays);
  return out;
};

export function SuppliersPage(): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | 'ACTIVE' | 'DISABLED'>('ACTIVE');
  const [editing, setEditing] = useState<SupplierRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const suppliers = useQuery({
    queryKey: ['suppliers', search, status],
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '100' });
      if (search) params.set('q', search);
      if (status) params.set('status', status);
      return api<{ data: SupplierRow[]; total: number }>(`/suppliers?${params.toString()}`);
    },
  });

  const close = (): void => {
    setAdding(false);
    setEditing(null);
    setDraft(EMPTY);
    setError(null);
  };

  const refresh = (message: string) => () => {
    void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    setNotice(message);
    close();
  };

  const save = useMutation({
    mutationFn: () =>
      editing
        ? api(`/suppliers/${editing.id}`, { method: 'PATCH', body: payload(draft) })
        : api('/suppliers', { method: 'POST', body: payload(draft) }),
    onSuccess: refresh(editing ? 'Supplier updated.' : 'Supplier added.'),
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save the supplier.'),
  });

  const setStatusOf = useMutation({
    mutationFn: ({ id, next }: { id: string; next: 'ACTIVE' | 'DISABLED' }) =>
      api(`/suppliers/${id}/status`, { method: 'POST', body: { status: next } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      setNotice('Supplier status changed.');
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not change the status.'),
  });

  const rows = suppliers.data?.data ?? [];
  const open = adding || editing !== null;

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy dark:text-white">Suppliers</h1>
          <p className="text-sm text-slate-500">
            Everyone we buy from. Disabled suppliers keep their history but cannot take new
            quotations or purchase orders.
          </p>
        </div>
        {can('suppliers.manage') && (
          <Button
            onClick={() => {
              setDraft(EMPTY);
              setAdding(true);
            }}
          >
            <Plus aria-hidden="true" className="h-4 w-4" /> Add supplier
          </Button>
        )}
      </header>

      {notice && <Alert tone="ok">{notice}</Alert>}
      {error && !open && <Alert tone="critical">{error}</Alert>}

      <Card>
        <div className="flex flex-wrap gap-3">
          <label className="relative min-w-[16rem] flex-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <input
              className={`${inputClass} pl-9`}
              placeholder="Search name, code, TIN or contact"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Search suppliers"
            />
          </label>
          <select
            className={`${inputClass} w-44`}
            value={status}
            onChange={(event) => setStatus(event.target.value as typeof status)}
            aria-label="Filter by status"
          >
            <option value="ACTIVE">Active</option>
            <option value="DISABLED">Disabled</option>
            <option value="">All</option>
          </select>
        </div>
      </Card>

      {suppliers.isLoading && <Skeleton className="h-64 w-full" />}

      {!suppliers.isLoading && rows.length === 0 && (
        <Card>
          <EmptyState
            title="No suppliers yet"
            description="Add the first supplier and you can attach their quotation to a lead."
          />
        </Card>
      )}

      {rows.length > 0 && (
        <Card>
          <TableWrap>
            <thead>
              <tr>
                <Th>Supplier</Th>
                <Th>Contact</Th>
                <Th>TIN</Th>
                <Th align="right">Quotations</Th>
                <Th align="right">POs</Th>
                <Th>Status</Th>
                <Th>{''}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((row) => (
                <tr key={row.id}>
                  <Td>
                    <p className="font-semibold">{row.name}</p>
                    <p className="font-mono text-xs text-slate-500">{row.code}</p>
                    {row.city && <p className="text-xs text-slate-500">{row.city}</p>}
                  </Td>
                  <Td>
                    {row.contactPerson && <p>{row.contactPerson}</p>}
                    {row.phone && <p className="text-xs text-slate-500">{row.phone}</p>}
                    {row.email && <p className="text-xs text-slate-500">{row.email}</p>}
                    {!row.contactPerson && !row.phone && !row.email && (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </Td>
                  <Td>
                    <span className="text-xs">{row.tin ?? '—'}</span>
                  </Td>
                  <Td align="right">{row._count.quotations}</Td>
                  <Td align="right">{row._count.purchaseOrders}</Td>
                  <Td>
                    <StatusChip tone={row.status === 'ACTIVE' ? 'ok' : 'neutral'}>
                      {row.status === 'ACTIVE' ? 'Active' : 'Disabled'}
                    </StatusChip>
                  </Td>
                  <Td>
                    {can('suppliers.manage') && (
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                          aria-label={`Edit ${row.name}`}
                          onClick={() => {
                            setDraft(toDraft(row));
                            setEditing(row);
                          }}
                        >
                          <Pencil aria-hidden="true" className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                          aria-label={
                            row.status === 'ACTIVE' ? `Disable ${row.name}` : `Enable ${row.name}`
                          }
                          onClick={() =>
                            setStatusOf.mutate({
                              id: row.id,
                              next: row.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE',
                            })
                          }
                        >
                          {row.status === 'ACTIVE' ? (
                            <Ban aria-hidden="true" className="h-4 w-4" />
                          ) : (
                            <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </Card>
      )}

      {open && (
        <Modal
          title={editing ? `Edit ${editing.name}` : 'Add supplier'}
          description="Only the name is required. The rest is what the purchase order will print."
          onClose={close}
        >
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              save.mutate();
            }}
          >
            {error && <Alert tone="critical">{error}</Alert>}
            <Field label="Supplier name" required>
              <input
                className={inputClass}
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                required
                minLength={2}
                autoFocus
              />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="TIN" hint="000-000-000-00000">
                <input
                  className={inputClass}
                  value={draft.tin}
                  onChange={(event) => setDraft({ ...draft, tin: event.target.value })}
                />
              </Field>
              <Field label="Contact person">
                <input
                  className={inputClass}
                  value={draft.contactPerson}
                  onChange={(event) => setDraft({ ...draft, contactPerson: event.target.value })}
                />
              </Field>
              <Field label="Phone">
                <input
                  className={inputClass}
                  value={draft.phone}
                  onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
                />
              </Field>
              <Field label="Email">
                <input
                  className={inputClass}
                  type="email"
                  value={draft.email}
                  onChange={(event) => setDraft({ ...draft, email: event.target.value })}
                />
              </Field>
              <Field label="Address">
                <input
                  className={inputClass}
                  value={draft.addressLine}
                  onChange={(event) => setDraft({ ...draft, addressLine: event.target.value })}
                />
              </Field>
              <Field label="City">
                <input
                  className={inputClass}
                  value={draft.city}
                  onChange={(event) => setDraft({ ...draft, city: event.target.value })}
                />
              </Field>
            </div>
            <fieldset className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Bank details
              </legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Field label="Account name">
                    <input
                      className={inputClass}
                      value={draft.bankAccountName}
                      onChange={(event) =>
                        setDraft({ ...draft, bankAccountName: event.target.value })
                      }
                    />
                  </Field>
                </div>
                <Field label="Bank name">
                  <input
                    className={inputClass}
                    value={draft.bankName}
                    onChange={(event) => setDraft({ ...draft, bankName: event.target.value })}
                  />
                </Field>
                <Field label="Account number">
                  <input
                    className={inputClass}
                    value={draft.bankAccountNumber}
                    onChange={(event) =>
                      setDraft({ ...draft, bankAccountNumber: event.target.value })
                    }
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Other payment details" hint="GCash number, or anything else finance needs">
                    <input
                      className={inputClass}
                      value={draft.bankDetails}
                      onChange={(event) => setDraft({ ...draft, bankDetails: event.target.value })}
                    />
                  </Field>
                </div>
              </div>
            </fieldset>
            <Field label="Payment terms (days)" hint="Leave blank if it varies">
              <input
                className={`${inputClass} w-32`}
                type="number"
                min={0}
                max={365}
                value={draft.paymentTermsDays}
                onChange={(event) => setDraft({ ...draft, paymentTermsDays: event.target.value })}
              />
            </Field>
            <Field label="Notes">
              <textarea
                className={inputClass}
                rows={2}
                value={draft.notes}
                onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
              />
            </Field>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending || draft.name.trim().length < 2}>
                <Truck aria-hidden="true" className="h-4 w-4" />
                {editing ? 'Save changes' : 'Add supplier'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
